/**
 * GET /api/admin/question-sets — list of assessment question-sets.
 * GET /api/admin/question-sets/{id} — full question-set with questions.
 *
 * Backend returns rows with field `id` (verified at
 * backend/src/handlers/admin.ts:1178 — `{ id, name, version, status, sections, ... }`).
 */
import { z } from 'zod';

export const QuestionSetSummarySchema = z.looseObject({
  id: z.string().min(1),
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  isDefault: z.boolean().optional(),
  questionCount: z.number().optional(),
  version: z.number().optional(),
  publishedAt: z.string().nullable().optional(),
  publishedBy: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  createdBy: z.string().nullable().optional(),
  updatedAt: z.string().optional(),
  sections: z.array(z.unknown()).optional(),
  playbook: z.record(z.string(), z.unknown()).nullable().optional(),
  idealRanking: z.array(z.string()).nullable().optional(),
});

export type QuestionSetSummary = z.infer<typeof QuestionSetSummarySchema>;

export const QuestionSetsListResponseSchema = z.looseObject({
  questionSets: z.array(QuestionSetSummarySchema),
});

export type QuestionSetsListResponse = z.infer<
  typeof QuestionSetsListResponseSchema
>;

const QuestionSchema = z.looseObject({
  questionId: z.string().min(1).optional(),
  id: z.string().min(1).optional(),
  text: z.string().optional(),
  type: z.string().optional(),
  options: z.array(z.unknown()).optional(),
  order: z.number().optional(),
  required: z.boolean().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const QuestionSetSchema = QuestionSetSummarySchema.extend({
  questions: z.array(QuestionSchema).optional(),
});

export type QuestionSet = z.infer<typeof QuestionSetSchema>;

export const QuestionSetResponseSchema = z.union([
  z.looseObject({ questionSet: QuestionSetSchema }),
  QuestionSetSchema,
]);

export type QuestionSetResponse = z.infer<typeof QuestionSetResponseSchema>;
